/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, {
  PADDING_VERTICAL,
  type ComponentStyles,
  BORDER_WIDTH,
  LABEL_KEY,
  PLACEHOLDER_KEY,
} from './WireframeBase';
import tools from '../tools';
import wireframeInputField from '../assets/icons/wireframeInputField.svg';
import wireframeInput from '../assets/icons/wireframeStateInput.svg';
import wireframeInputBordered from '../assets/icons/wireframeStateInputBordered.svg';
import wireframeDropdown from '../assets/icons/wireframeStateDropdown.svg';
import wireframeDropdownBordered from '../assets/icons/wireframeStateDropdownBordered.svg';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { SPACER_1, SPACER_2, SPACER_3, SPACER_4 } from '../utils/style';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import { WIREFRAME_SELECTION_COLOR } from '../utils/wireframes';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { Label } from '../components/wireframes/Label';
import { Placeholder } from '../components/wireframes/Placeholder';
import WireframeAddItemControl from '../controls/WireframeAddItemControl';
import { EditableText } from '../canvasx/EditableText';

const PADDING_HORIZONTAL_BORDERED = SPACER_4;
const PADDING_HORIZONTAL = SPACER_1;
const BORDER_RADIUS = 4;
const ADD_ITEM_CONTROL_KEY = 'addItem';
const SEPARATOR_SIZE = 1;
const MIN_WIDTH = 10;
const DROPDOWN_MODE_MIN_ITEM_HEIGHT = 40;
const DROPDOWN_MODE_MIN_LABEL_HEIGHT = 74;
const INPUT_FIELD_MODE_MIN_PLACEHOLDER_HEIGHT = 37;
const INPUT_FIELD_MODE_MIN_LABEL_HEIGHT = 30;
const DEFAULT_WIDTH = 240;

class WireframeInputField extends WireframeBase {
  hasDraggableItems = true;

  constructor(fabricObject) {
    super(fabricObject);
    this.fabricObject.on('selected', () => this.showAddItemControl());
  }

  initItems() {
    if (!this.label) {
      this.addLabel({ value: 'Label', position: 'outside' });
    }
    if (!this.placeholder) {
      this.addPlaceholder({ value: 'Text', state: 'text' });
    }
  }

  addDropdownItems() {
    if (this.fabricObject.items.items.length === 0) {
      this.fabricObject.items.add({ value: 'Item' });
      this.fabricObject.items.add({ value: 'Item' });
      this.fabricObject.items.add({ value: 'Item' });
    }
  }

  showAddItemControl() {
    if (this.isDropdown) {
      if (!this.fabricObject.controls[ADD_ITEM_CONTROL_KEY]) {
        this.fabricObject.controls[ADD_ITEM_CONTROL_KEY] =
          new WireframeAddItemControl({ layout: 'column' });
      }
      this.fabricObject.controls[ADD_ITEM_CONTROL_KEY].setVisibility(true);
    } else {
      this.fabricObject.controls[ADD_ITEM_CONTROL_KEY]?.setVisibility(false);
    }
  }

  // Only dropdowns have add and drag item controls, so show/hide those accordingly
  updateControls() {
    this.showAddItemControl();
    this.isDropdown
      ? (this.hasDraggableItems = true)
      : (this.hasDraggableItems = false);
    this.fabricObject.canvas?.requestRenderAll();
  }

  get componentLayout() {
    return 'column';
  }

  componentOffset() {
    let x = 0;
    let y = 0;
    if (this.isDropdown) {
      const { placeholder, label } = this;
      if (placeholder.dimensions) {
        x += placeholder.dimensions.width;
        y = placeholder.dimensions.height;
      }
      if (label.dimensions) {
        x += label.dimensions.width;
        y += label.dimensions.height;
      }
    }
    return { x, y };
  }

  tools() {
    const activeId = this.activeItemId();
    if (
      activeId &&
      !this.activeKey.includes(PLACEHOLDER_KEY) &&
      !this.activeKey.includes(LABEL_KEY)
    ) {
      // show tools for individual dropdown items
      const itemTools = [[tools.ToolWireframeItemState]];

      // Show delete tool only if more than one item left
      if (this.fabricObject.items.items.length > 1) {
        itemTools.push([tools.ToolWireframeDeleteItem]);
      }

      return itemTools;
    }
    return [
      [tools.ToolWireframeComponentStyle],
      !this.isDropdown ? [tools.ToolWireframePlaceholderTextToggle] : [],
      this.isBordered ? [tools.ToolWireframeLabelPosition] : [],
      [tools.ToolWireframeIcon],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      [tools.ToolWireframeTextColor],
    ];
  }

  static iconSvg() {
    return wireframeInputField;
  }

  static externalName(): string {
    return 'Text field';
  }

  static defaultWidth(): number {
    return DEFAULT_WIDTH;
  }

  static componentStyles(): ComponentStyles {
    return {
      underlined: wireframeInput,
      framed: wireframeInputBordered,
      underlineWithPanel: wireframeDropdown,
      framedWithPanel: wireframeDropdownBordered,
    };
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      mt: new WireframeLinearResizeControl({ edge: 'top' }),
      mb: new WireframeLinearResizeControl({ edge: 'bottom' }),
    };
  }

  componentStyles(): ComponentStyles {
    return WireframeInputField.componentStyles();
  }

  placeholderStates() {
    return ['text', 'placeholder'];
  }

  togglePlaceholderState() {
    // These are not user visibile states, so just use default values
    const state = this.placeholder.state === 'text' ? 'placeholder' : 'text';
    this.setPlaceholderState(state);
    this.fabricObject.canvas.requestRenderAll();
  }

  minHeight() {
    if (this.isDropdown) {
      return (
        DROPDOWN_MODE_MIN_LABEL_HEIGHT +
        DROPDOWN_MODE_MIN_ITEM_HEIGHT * this.fabricObject.items.items.length
      );
    }
    const labelHeight = this.showLabel ? INPUT_FIELD_MODE_MIN_LABEL_HEIGHT : 0;
    return labelHeight + INPUT_FIELD_MODE_MIN_PLACEHOLDER_HEIGHT;
  }

  minDimension({ resizingWidth }: { resizingWidth: boolean }): number {
    if (resizingWidth) {
      return Math.max(this.maxItemDimensions.width, MIN_WIDTH);
    }
    return Math.max(this.maxItemDimensions.height, this.minHeight());
  }

  get itemSeparatorSize() {
    return this.isDropdown ? SEPARATOR_SIZE : 0;
  }

  get isDropdown() {
    return this.fabricObject.style.toLowerCase().includes('panel');
  }

  get isBordered() {
    return this.fabricObject.style.toLowerCase().includes('framed');
  }

  get inputPadding() {
    return this.isBordered
      ? { horizontal: PADDING_HORIZONTAL_BORDERED, vertical: PADDING_VERTICAL }
      : { horizontal: PADDING_HORIZONTAL, vertical: PADDING_VERTICAL };
  }

  get placeholderProps() {
    const { fontSize, textColor } = this.fabricObject;
    const { isBordered, isDropdown, inputPadding, placeholder } = this;
    const fill = this.fabricObject.fill ?? 'white';

    return {
      color: textColor,
      opacity: !isDropdown && placeholder.state === 'placeholder' ? 0.4 : 1,
      iconColor: fill,
      border:
        isBordered || isDropdown
          ? 'transparent'
          : { bottom: fill, lineWidth: BORDER_WIDTH },
      padding: inputPadding,
      size: fontSize,
      gap: SPACER_3,
      setRenderedDimensions: dimensions => {
        this.updateMaxItemDimensions({
          dimensions,
          useUnwrappedWidth: true,
        });
        this.placeholder.dimensions = dimensions;
      },
    };
  }

  get labelProps() {
    const { textColor, fontSize } = this.fabricObject;
    const { labelInside, isBordered } = this;
    let padding: SpacingType;
    if (isBordered) {
      padding = labelInside
        ? {
            top: SPACER_3,
            left: PADDING_HORIZONTAL_BORDERED,
            right: PADDING_HORIZONTAL_BORDERED,
          }
        : {
            top: SPACER_1,
            left: PADDING_HORIZONTAL,
            right: PADDING_HORIZONTAL,
            bottom: SPACER_2,
          };
    } else {
      padding = this.isDropdown
        ? 0
        : {
            top: SPACER_1,
            left: PADDING_HORIZONTAL,
            right: PADDING_HORIZONTAL,
          };
    }

    return {
      color: textColor,
      padding,
      bold: true,
      size: fontSize,
      setRenderedDimensions: dimensions => {
        this.updateMaxItemDimensions({
          dimensions,
          useUnwrappedWidth: true,
        });
        this.label.dimensions = dimensions;
      },
    };
  }

  get labelInside() {
    return this.label?.position === 'inside';
  }

  wasSet(keyOrPath: KeyPath, value: any): void {
    // We do not update collaboratively until the canvas is set
    if (keyOrPath === 'canvas') {
      const items = this.fabricObject.items.items;
      // Backwards compat: originally the label and placeholder fields were stuffed
      // into the items array. Move these out in favor of a simpler data model where
      // these fields are static fields on the Wireframe
      if (items.length > 0) {
        const label = items[0];
        const placeholder = items[1];
        // Check if these are the label and placeholder items based on their state
        if (
          this.labelPositions().includes(label.state) &&
          this.placeholderStates().includes(placeholder.state)
        ) {
          this.addLabel({ value: label.value, position: label.state });
          this.addPlaceholder(placeholder);
          // Clean up items array so it only contains dropdown items
          this.deleteItem(label.id);
          this.deleteItem(placeholder.id);
        }
      }
    }
    if (keyOrPath === 'style') {
      if (value.toLowerCase().includes('panel')) {
        this.addDropdownItems();
      }
      this.updateControls();
    }
    super.wasSet(keyOrPath, value);
  }

  render(ctx: CanvasRenderingContext2D) {
    const { width, height, scaleX, textColor, textEditing, fontSize, items } =
      this.fabricObject;
    const {
      labelProps,
      showLabel,
      labelInside,
      isDropdown,
      isBordered,
      inputPadding,
      placeholderProps,
    } = this;
    const fill = this.fabricObject.fill ?? 'white';

    // Allow component to shrink when children shrink
    this.resetMaxItemDimensions();

    let renderedDimensions: DimensionStruct;

    const inputBorder = { color: fill, lineWidth: BORDER_WIDTH };

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexColumn
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            horizontalAlignment='stretch'
            width={width}
            height={height}
          >
            {showLabel && !labelInside && (
              <Label wireframe={this} {...labelProps} />
            )}
            <FlexColumn
              border={isBordered ? inputBorder : 'transparent'}
              fill={this.isBordered ? 'white' : undefined}
              borderRadius={
                isDropdown
                  ? { topLeft: BORDER_RADIUS, topRight: BORDER_RADIUS }
                  : BORDER_RADIUS
              }
              horizontalAlignment='stretch'
              flexGrow={1}
              verticalAlignment='center'
            >
              {showLabel && labelInside && (
                <Label wireframe={this} {...labelProps} />
              )}
              <Placeholder wireframe={this} {...placeholderProps} />
            </FlexColumn>
            {isDropdown && (
              <FlexColumn
                separator={{
                  lineWidth: SEPARATOR_SIZE,
                  color: fill,
                  direction: 'horizontal',
                  opacity: 0.2,
                }}
                horizontalAlignment='stretch'
                border={inputBorder}
                borderRadius={{
                  bottomLeft: BORDER_RADIUS,
                  bottomRight: BORDER_RADIUS,
                }}
                fill={'white'}
                padding={{ bottom: BORDER_WIDTH }}
                flexGrow={items.items.length}
                setRenderedDimensions={dimensions => {
                  if (height > this.maxItemDimensions.height) {
                    this.maxItemDimensions.height = dimensions.height;
                  }
                }}
              >
                {items.items.map(item => {
                  const { id, value, state } = item;
                  const selected = state === 'selected';
                  const active = this.activeItemId() === id;
                  return (
                    <EditableText
                      key={id}
                      flexGrow
                      textKey={`text:${id}`}
                      padding={inputPadding}
                      color={selected ? fill : textColor}
                      fill={
                        selected
                          ? { color: fill, opacity: 0.05 }
                          : 'transparent'
                      }
                      border={
                        active ? WIREFRAME_SELECTION_COLOR : 'transparent'
                      }
                      textEditing={textEditing && id === this.activeItemId()}
                      opacity={selected ? 1 : 0.8}
                      size={fontSize}
                      onChange={value => {
                        this.onItemChange(value);
                      }}
                      verticalAlignment='center'
                      setRenderedDimensions={dimensions => {
                        if (width > this.maxItemDimensions.width) {
                          this.maxItemDimensions.width = dimensions.width;
                        }
                      }}
                    >
                      {value}
                    </EditableText>
                  );
                })}
              </FlexColumn>
            )}
          </FlexColumn>
        );

        if (this.updateDimensions) {
          this.updateDimensions = false;
          this._checkSetDimension();

          const componentWidth = Math.max(width, this.maxItemDimensions.width);

          // Enforce minimum width and height
          const newWidth = Math.max(MIN_WIDTH, componentWidth);
          const newHeight = Math.max(
            this.minHeight(),
            renderedDimensions.height
          );

          this.fabricObject.set({
            width: newWidth,
            height: newHeight,
          });
          this.fabricObject.canvas.requestRenderAll();
        }
      }
    );
  }
}

export default WireframeInputField;
